import React, { Fragment, ReactElement, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from '@emotion/styled';
import { CgAlignBottom } from '@react-icons/all-files/cg/CgAlignBottom';
import { CgAlignMiddle } from '@react-icons/all-files/cg/CgAlignMiddle';
import { CgAlignTop } from '@react-icons/all-files/cg/CgAlignTop';
import { NumberInputField, Option, RadioField, SelectField, ToggleField } from '@bucketplace/commerce-admin-ui';
import { BREAK_POINT_NAMES, BreakPoint } from '@bucketplace/design-system';
import { Divider } from '@bucketplace/design-system/bds';

import { FormContent, InspectorFormProvider } from '../../../components';
import { DocumentInspectorProps } from '../../../types';
import { GridProps } from '../Grid';
import { BREAK_POINT_OPTIONS, GridData, INITIAL_GRID_DATA } from '../types';

const COLUMN_OPTIONS: Option<unknown>[] = [
  { text: '1', value: 1 },
  { text: '2', value: 2 },
  { text: '3', value: 3 },
  { text: '4', value: 4 },
];

const VERTICAL_ALIGN_OPTIONS = [
  {
    text: <CgAlignTop />,
    value: 'flex-start',
  },
  {
    text: <CgAlignMiddle />,
    value: 'center',
  },
  {
    text: <CgAlignBottom />,
    value: 'flex-end',
  },
];

export function GridInspector({
  value,
  onChange,
}: DocumentInspectorProps<GridProps>): ReactElement {
  return (
    <InspectorFormProvider value={value} onChange={onChange}>
      <Grid />
    </InspectorFormProvider>
  );
}

function Grid(): ReactElement {
  const { getValues, setValue } = useFormContext();
  const value = getValues();

  const handleChangeFlag = (type: BreakPoint, flagValue?: boolean): void => {
    setValue(type, flagValue ? INITIAL_GRID_DATA : null);
  };

  useEffect(() => {
    const hasBreakPoint = Object.entries(value)
      .some(([key, value]) => BREAK_POINT_NAMES.some((name) => name === key) && value);

    if (!hasBreakPoint) {
      setValue('mobile', INITIAL_GRID_DATA);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, value]);

  /** 마지막 체크를 지울 수 없도록 막는데 사용 */
  const checkedCount = useMemo(() => {
    let count = 0;

    BREAK_POINT_NAMES.forEach((point) => {
      if (value[point] != null) {
        count += 1;
      }
    });

    return count;
  }, [BREAK_POINT_NAMES, value]);

  return (
    <>
      { BREAK_POINT_OPTIONS.map((option, index) => {
        const data = value[option.value as keyof typeof value] as GridData | undefined;

        return (
          <Fragment key={`${option.value}`}>
            <FormContent header={option.label}>
              <ToggleField
                name={option.value}
                disabled={checkedCount === 1 && data != null}
                onCheck={(value: boolean) => handleChangeFlag(option.value, value)}
              />
            </FormContent>
            { data && (
              <GridInput
                type={option.value}
              />
            ) }
            { BREAK_POINT_OPTIONS.length - 1 > index && <Divider variant="thick" /> }
          </Fragment>
        );
      },
      ) }
    </>
  );
}

interface GridInputProps {
  type: string;
}
function GridInput({
  type,
}: GridInputProps): ReactElement {
  const { getValues, setValue } = useFormContext();
  const { column, columnRatio = [] } = getValues(type);

  useEffect(() => {
    if (columnRatio.length === column) {
      return;
    }

    setValue(`${type}.columnRatio`, Array(column).fill(1));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column]);

  return (
    <div>
      <FormContent header="열 갯수">
        <SelectField
          name={`${type}.column`}
          options={COLUMN_OPTIONS}
        />
      </FormContent>
      <FormContent header="비율(N:N)">
        <GapWrapper>
          { columnRatio.map((_: number, ratioIndex: number) => (
            <NumberInputField
              key={`${ratioIndex + 1}`}
              name={`${type}.columnRatio.${ratioIndex}`}
              width="100%"
            />
          )) }
        </GapWrapper>
      </FormContent>
      <FormContent header="간격">
        <NumberInputField
          name={`${type}.gap`}
        />
      </FormContent>
      <FormContent header="세로축 정렬">
        <RadioField
          name={`${type}.verticalAlign`}
          options={VERTICAL_ALIGN_OPTIONS}
        />
      </FormContent>
    </div>
  );
}

const GapWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;
